import { Stack, StackItem } from '@patternfly/react-core'
import { sum } from 'd3-array'
import { useContext } from 'react'
import SitesContext from '../../contexts/SitesContext'
import calcVerticalHeight from '../../utils/calcVerticalHeight'
import ColorScale from '../../utils/colorScale'
import { defaultAxis, layoutTemplate } from '../../utils/plotly'
import Plot from '../shared/Plot'
import { categories, DEFAULT_PLOT_CONFIG, PLOT_TEMPORAL_MONTH_WTG } from './constants'
import useData from './useData'

const PLOT_HEIGHT = calcVerticalHeight({ gridSize: 2, elementSize: 1, customOffset: '10rem' })
const PLOT_STYLE = { width: '100%', height: PLOT_HEIGHT }

const PLOT_LAYOUT = {
  template: layoutTemplate,
  paper_bgcolor: ColorScale.branco,
  plot_bgcolor: ColorScale.branco_clear,
  showlegend: false,
  hovermode: 'x unified',
  autosize: true,
  barmode: 'stack',
  margin: { b: 70, t: 20, r: 20, l: 50 },
  xaxis: {
    ...defaultAxis,
    fixedrange: true,
    mirror: 'ticks',
    linewidth: 2,
    gridwidth: 2,
    tickangle: -90,
    tickfont: { size: 9 },
  },
  yaxis: { ...defaultAxis, fixedrange: true, ticksuffix: '%', range: [0, 110] },
}

const PlotPeriodWTGTotal = ({
  isLoading,
  dateIn,
  dateFin,
  revision,
  categorySelection,
}: {
  isLoading: boolean
  dateIn: Date
  dateFin: Date
  revision: number
  categorySelection: categories
}) => {
  const { isLoading: dataIsLoading, dailyTurbs } = useData(dateIn, dateFin)
  const { turbs } = useContext(SitesContext)

  if (isLoading || dataIsLoading) {
    return (
      <div style={PLOT_STYLE} className='pf-v5-u-mt-0'>
        <Plot
          divId={PLOT_TEMPORAL_MONTH_WTG}
          useResizeHandler
          key={revision}
          data={[]}
          layout={PLOT_LAYOUT}
          config={DEFAULT_PLOT_CONFIG}
          style={PLOT_STYLE}
        />
      </div>
    )
  }

  const y = turbs.map(tb => {
    const mp = dailyTurbs.find(mp => mp.turb_id === tb.turb_id)

    if (!mp) return undefined

    const downtime = sum(categorySelection.downtime.map(cat => mp[cat]))
    const online = sum(categorySelection.online.map(cat => mp[cat]))

    return (online / (online + downtime)) * 100
  })

  const middle = Math.floor(turbs.length / 2)
  const upperTurbs = turbs.slice(0, middle)
  const bottomTurbs = turbs.slice(middle, turbs.length)

  const upperData = [
    {
      ...defaultAxis,
      y,
      x: upperTurbs.map(tb => tb.name),
      name: 'Disponibilidade Temporal',
      type: 'bar',
      marker: {
        color: ColorScale.verde,
      },
    },
  ]
  const bottomData = [
    {
      ...defaultAxis,
      y,
      x: bottomTurbs.map(tb => tb.name),
      name: 'Disponibilidade Temporal',
      type: 'bar',
      marker: {
        color: ColorScale.verde,
      },
    },
  ]

  return (
    <Stack>
      <StackItem>
        <div style={PLOT_STYLE} className='pf-v5-u-mt-0'>
          <Plot
            divId={PLOT_TEMPORAL_MONTH_WTG}
            useResizeHandler
            key={revision}
            data={upperData}
            layout={PLOT_LAYOUT}
            config={DEFAULT_PLOT_CONFIG}
            style={PLOT_STYLE}
            onLegendClick={() => false}
          />
        </div>
      </StackItem>
      <StackItem>
        <div style={PLOT_STYLE} className='pf-v5-u-mt-0'>
          <Plot
            divId={PLOT_TEMPORAL_MONTH_WTG}
            useResizeHandler
            key={revision}
            data={bottomData.filter(t => t)}
            layout={PLOT_LAYOUT}
            config={DEFAULT_PLOT_CONFIG}
            style={PLOT_STYLE}
            onLegendClick={() => false}
          />
        </div>
      </StackItem>
    </Stack>
  )
}

export default PlotPeriodWTGTotal
